import * as React from "react";
import {cx} from "emotion";

import "./qr-code.scss";

import {cs}                         from "@Common/react/chain-services";
import {keyed}                      from "@Common/react/keyed";
import {ConditionalValue}           from "@Common/react/shadow/conditional-value";
import {IndependentList}            from "@Common/react/shadow/independent-list";
import {scnv}                       from "@Common/react/state-change-nested-value";
import {UseState}                   from "@Common/react/use-state";
import {InformationBadge}           from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {formatMoney1}               from "@Common/ui-components/language/format-money";
import {cascadeChange, cascadeFind} from "@Common/utils/cascade";
import {chain}                      from "@Common/utils/fs";
import {omit}                       from "@Common/utils/objects";

import {groupEnrollmentEditors} from "../../../common/group-enrollment-editors";

import {QrUrlContent} from "./qr-url-content/qr-url-content";

export const QrCode = ({initEnrollment, templateOptions, program, next}) => cs(
    ["lineItem", (_, next) => next(templateOptions?.options?.find(x => x.dev_key === "qr_code"))],
    ["availTypes", ({lineItem}, next) => next(
        (
            !program?.programPreferences?.enableQRCode ||
            !lineItem
        ) ? [] : [
            program?.programPreferences?.allowXpresslinks && "xpresslinks",
            program?.programPreferences?.allowHomeValue && "homevalue",
            program?.programPreferences?.allowCustomUrl && "url",
        ].filter(v=>v)
    )],
    ["disabled", ({lineItem, availTypes}, next) => next(!availTypes.length)],
    ({disabled, lineItem, availTypes}) => ConditionalValue({
        _key: disabled,
        renderShadow: (next) => (
            !disabled ? (
                QrCode1({
                    initEnrollment,
                    lineItem,
                    availTypes,
                    next,
                })
            ) : next({
                forPersonalization: () => ({
                    enrollmentEditor: {
                        $name: "Qr Code - Disabled",
                        prepareSave: (enrollment) => ({
                            newValue: cascadeChange(enrollment, "seriesPreferences[*]", (_) => omit(_, ["qrCode"])),
                        }),
                        changed: false,
                        valid: true,
                    },

                    render: () => null,
                }),
            })
        ),
        next,
    })
);

const labels2 = {
    "xpresslinks": "Xpress Links",
    "homevalue": "Home Value",
    "url": "URL",
};
const QrCode1 = ({initEnrollment, lineItem, availTypes, next}) => cs(
    ["type", (_, next) => UseState({
        getInitValue: () => chain(
            cascadeFind(initEnrollment, "seriesPreferences[*].qrCode.redirectType"),
            (_) => ({
                current: (
                    _ ?? availTypes[0]
                ),
                saved  : _,
            }),
        ),
        next,
    })],
    ["content", ({type}, next) => IndependentList({
        list: [
            availTypes.includes("url") && {
                type: "url",
                render: ({disabled, next}) => QrUrlContent({
                    initEnrollment,
                    disabled,
                    next,
                }),
            }
        ].filter(v=>v).map((r) => ({
            key: r.type,
            render: (next) => cs(
                ["active", (_, next) => next(type.value.current === r.type)],
                ["ret", ({active}, next) => r.render({disabled: !active, next})],
                ({ret, active}) => next({
                    ...ret,
                    active,
                })
            ),
        })),
        next: (list) => next(list.find((r) => r.active)),
    })],
    ["price", ({type}, next) => next(
        chain(
            lineItem.subOptions.find((s) => s.dev_key === type.value.current),
            (_) => _ ?? lineItem,
            (_) => (
                +(_.flatPrice ?? 0) +
                +(_.unitPrice ?? 0)
            ),
        )
    )],
    ({type, content, price}) => next({
        getSummaryLines: (quantity) => [
            {
                name: <>QR Code - {labels2[type.value.current]} ({formatMoney1(price)} each)</>,
                value: formatMoney1(price * quantity),
            },
        ],
        getEstimatedTotal: (quantity) => price * quantity,
        forPersonalization: () => ({
            enrollmentEditor: groupEnrollmentEditors([
                {
                    $name: "Qr Code",
                    prepareSave: (enrollment) => ({
                        newValue: cascadeChange(enrollment, "seriesPreferences[*].qrCode.redirectType", (_) => (
                            type.value.current
                        )),
                        onSaved: () => scnv(type, ["saved"], () => type.value.current),
                    }),
                    changed: type.value.saved !== type.value.current,
                    valid: true,
                },
                content?.enrollmentEditor,
            ].filter(v=>v)),
            render: ({showErrors}) => (
                <div {...{
                    className: "qr-code-6dj",

                    "xd-qa-type": "card",
                    "xd-qa-name": "QR Code Selection",
                }}>
                    <div className="header">

                        <div {...{
                            className: "title",

                            "xd-qa-type": "label",
                            "xd-qa-name": "QR Code Selection Title",
                        }}>
                            QR Code Selection{" "}
                            {InformationBadge({
                                getTooltip: () => "QR codes will only appear on print if the template design supports them",
                                xdQaName: "QR Code Selection explanation",
                            })}
                        </div>

                    </div>

                    <div className="box">
                        <div className="tabs">
                            {availTypes.map((type1) => cs(
                                keyed(type1),
                                () => {
                                    const selected = type1 === type.value.current;
                                    return (
                                        <button {...{
                                            className: cx({selected}),
                                            onClick: () => {
                                                scnv(type, ["current"], () => type1);
                                            },

                                            "xd-qa-type": "button",
                                            "xd-qa-name": type1.toUpperCase(),
                                        }}>
                                            {type1.toUpperCase()}
                                        </button>
                                    );
                                }
                            ))}
                        </div>

                        {content && (
                            <div className="content">
                                {content.render({showErrors})}
                            </div>
                        )}
                    </div>
                </div>
            )
        }),
    }),
);

